
import React from 'react';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import WaitlistForm from '@/components/forms/WaitlistForm';
import { WaitlistProvider } from '@/context/WaitlistContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { HelpCircle } from 'lucide-react';
import FooterSection from '@/components/homepage/FooterSection';

const FAQ = () => {
  useAnalytics(); // This ensures page view tracking is active
  
  const faqs = [
    {
      question: "When will Wabisabi launch?",
      answer: "We're launching our beta web app in July 2025 with 1,000 testing spots. Beta users will help us perfect the experience before our full public launch."
    },
    {
      question: "How does the waitlist work?",
      answer: "Join the waitlist to secure your spot among the first 1,000 users. We'll notify you when beta access is ready and guide you through the setup process."
    },
    {
      question: "What do I get as a beta user?",
      answer: "The first 1,000 waitlist members get exclusive beta access plus 3 months of free premium features to test and provide feedback."
    },
    {
      question: "Is this only for couples?",
      answer: "No! Wabisabi works for all your relationships — partners, family, friends, colleagues, and anyone you want to stay connected with."
    },
    {
      question: "What if I'm not in the first 1,000?",
      answer: "You'll still get early access ahead of the general public, plus we'll keep you updated with relationship tips and product development insights."
    },
    {
      question: "Is my data safe?",
      answer: "Yes, your data is protected with multiple layers of security. We use Supabase's enterprise-grade infrastructure with secure authentication (including Google and Apple sign-in), encrypted connections, and Row Level Security that ensures you can only access your own data. Your files are stored in isolated buckets with strict access controls. We never sell your personal information, and you maintain complete control to view, edit, or delete your data anytime. All sensitive information is encrypted in transit, and we follow security best practices including secure session management and input validation."
    },
    {
      question: "Will this cost anything during beta?",
      answer: "Beta access is completely free for 3 months. This gives you time to experience Wabisabi and see how it improves your relationships before any paid features."
    },
    {
      question: "What devices will it work on?",
      answer: "The beta launches as a web app that works on any device with a browser — desktop, tablet, or mobile. We plan to launch our mobile app by August 2025 for both android and iOS."
    }
  ];

  return (
    <WaitlistProvider>
      <SEOHead pageKey="faq" />
      <div className="min-h-screen bg-gradient-to-b from-[#F7F5F2] to-white">
      <Header showActions={false} />
      
      <main className="section-container section-padding">
        {/* Hero Section */}
        <div className="text-center mb-16 morning-fade">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-12 bg-[#6C4F77] rounded-full flex items-center justify-center warm-touch">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#6C4F77]">
              Waitlist FAQ
            </h1>
          </div>
          <p className="text-xl text-[#2C2C2C]/80 max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about joining the Wabisabi waitlist
          </p>
        </div>

        {/* FAQ Section */}
        <Card className="mb-16 morning-fade shadow-soft-hover" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-8 md:p-12">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-[#F7F5F2] rounded-xl px-6 py-2 transition-all duration-300 hover:shadow-md hover:border-[#6C4F77]/20"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="text-lg font-semibold text-[#6C4F77] pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-[#2C2C2C] leading-relaxed text-base">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="morning-fade text-center shadow-soft-hover" style={{ animationDelay: '1.0s' }}>
          <CardContent className="p-8 md:p-12">
            <div className="max-w-2xl mx-auto mb-8">
              <h2 className="text-3xl font-semibold text-[#6C4F77] mb-6">
                Ready to Join the Waitlist?
              </h2>
              <p className="text-[#2C2C2C] text-lg mb-8">
                Secure your spot among the first 1,000 users and help shape the future of meaningful relationships.
              </p>
              <div>
                <WaitlistForm variant="default" />
              </div>
            </div>
            
            <div className="text-sm text-[#6C4F77]/70 mt-6">
              <p>Have more questions? Email us at <a href="mailto:hello@appwabisabi.com" className="text-[#F4978E] hover:underline">hello@appwabisabi.com</a></p>
            </div>
          </CardContent>
        </Card>
      </main>
      <FooterSection />
    </div>
    </WaitlistProvider>
  );
};

export default FAQ;